// Shared
import { platformQueryClient } from '@/queries/query-client';

/** Factories return TanStack `queryKey` arrays. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- contravariance for concrete factory params
type QueryKeyFactories = Record<string, (...args: any[]) => readonly unknown[]>;

export type QueryKeysInvalidateRunners<T extends QueryKeyFactories> = {
  [K in keyof T]: (
    ...args: Parameters<T[K]>
  ) => ReturnType<typeof platformQueryClient.invalidateQueries>;
};

/**
 * For each entry in a `queriesKeys`-shaped map, builds a function that calls
 * `platformQueryClient.invalidateQueries({ queryKey: factory(...args) })`.
 *
 * Use as `await queryKeysInvalidate.getCompanyAgreements(...)`.
 * The app `QueryClientProvider` uses {@link platformQueryClient}, so this matches `useQueryClient()` behavior.
 */
export function createQueryKeysInvalidate<T extends QueryKeyFactories>(
  factories: T
): QueryKeysInvalidateRunners<T> {
  const out = {} as Record<
    string,
    (...args: unknown[]) => ReturnType<typeof platformQueryClient.invalidateQueries>
  >;

  (Object.keys(factories) as (keyof T)[]).forEach((key) => {
    const factory = factories[key] as (...args: unknown[]) => readonly unknown[];

    out[key as string] = (...args: unknown[]) =>
      platformQueryClient.invalidateQueries({
        queryKey: factory(...args),
      });
  });

  return out as unknown as QueryKeysInvalidateRunners<T>;
}
